import React, { useEffect, useMemo, useState } from 'react'
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client'
import {
    Button,
    Confirm,
    Header,
    Icon,
    List,
    Loader,
    Message,
    Segment,
} from 'semantic-ui-react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Scatter,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import {
    CONTINUE_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION,
    PROJECT_RECOMMENDATIONS_QUERY,
    RUN_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION,
} from '../../../graphql/project'
import { LOAD_EXPERT_SUMMARY_QUERY } from '../../../graphql/user'

const numberOrNull = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const getEntryExpertId = (entry) => String(entry?.expertId || '').trim()

const TOOLTIP_CARD_STYLE = { background: 'white', border: '1px solid #ddd', padding: '0.6rem' }

const CRITERION_HISTOGRAM_BAND_COUNT = 20
const MAX_POINTS_FOR_KENDALL_TAU = 500

const toLogElasticsearchScore = (score) => {
    if (score === null || score <= -1) return null
    const transformed = Math.log(score + 1)
    return Number.isFinite(transformed) ? transformed : null
}

const computeRegression = (points) => {
    if (points.length < 2) return null

    const n = points.length
    const sumX = points.reduce((sum, point) => sum + point.x, 0)
    const sumY = points.reduce((sum, point) => sum + point.y, 0)
    const sumXX = points.reduce((sum, point) => sum + point.x * point.x, 0)
    const sumXY = points.reduce((sum, point) => sum + point.x * point.y, 0)

    const denominator = n * sumXX - sumX * sumX
    if (denominator === 0) return null

    const slope = (n * sumXY - sumX * sumY) / denominator
    const intercept = (sumY - slope * sumX) / n

    return { slope, intercept }
}

const computePearsonCorrelationFromArrays = (xValues, yValues) => {
    if (xValues.length < 2 || yValues.length < 2 || xValues.length !== yValues.length) {
        return null
    }

    const n = xValues.length
    const sumX = xValues.reduce((sum, value) => sum + value, 0)
    const sumY = yValues.reduce((sum, value) => sum + value, 0)
    const sumXX = xValues.reduce((sum, value) => sum + value * value, 0)
    const sumYY = yValues.reduce((sum, value) => sum + value * value, 0)
    const sumXY = xValues.reduce((sum, value, index) => sum + value * yValues[index], 0)

    const numerator = n * sumXY - sumX * sumY
    const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY))
    if (!Number.isFinite(denominator) || denominator === 0) return null

    return numerator / denominator
}

const rankValues = (values) => {
    const indexed = values
        .map((value, index) => ({ value, index }))
        .sort((a, b) => a.value - b.value)

    const ranks = new Array(values.length)
    let i = 0

    while (i < indexed.length) {
        let j = i
        while (j + 1 < indexed.length && indexed[j + 1].value === indexed[i].value) {
            j += 1
        }

        const averageRank = (i + j) / 2 + 1
        for (let k = i; k <= j; k += 1) {
            ranks[indexed[k].index] = averageRank
        }

        i = j + 1
    }

    return ranks
}

const computeSpearmanCorrelation = (points) => {
    if (points.length < 2) return null

    const xRanks = rankValues(points.map((point) => point.x))
    const yRanks = rankValues(points.map((point) => point.y))

    return computePearsonCorrelationFromArrays(xRanks, yRanks)
}

const computeKendallTau = (points) => {
    if (points.length < 2) return null

    let concordant = 0
    let discordant = 0
    let tiesX = 0
    let tiesY = 0

    for (let i = 0; i < points.length - 1; i += 1) {
        for (let j = i + 1; j < points.length; j += 1) {
            const dx = points[i].x - points[j].x
            const dy = points[i].y - points[j].y

            if (dx === 0 && dy === 0) {
                tiesX += 1
                tiesY += 1
            } else if (dx === 0) {
                tiesX += 1
            } else if (dy === 0) {
                tiesY += 1
            } else if ((dx > 0 && dy > 0) || (dx < 0 && dy < 0)) {
                concordant += 1
            } else {
                discordant += 1
            }
        }
    }

    const denominator = Math.sqrt(
        (concordant + discordant + tiesX) * (concordant + discordant + tiesY)
    )

    if (!Number.isFinite(denominator) || denominator === 0) return null

    return (concordant - discordant) / denominator
}

const formatList = (values, fallback = 'N/A') => {
    if (!values || values.length === 0) return fallback
    return values.join(', ')
}

const extractExpertPointFromTooltipPayload = (payload = []) => {
    for (const entry of payload) {
        const directPayload = entry?.payload
        if (directPayload?.expertId) {
            return directPayload
        }

        const nestedPayload = directPayload?.payload
        if (nestedPayload?.expertId) {
            return nestedPayload
        }
    }

    return null
}

const buildLogScoreByExpertIdForCriterion = (criterionScores = []) => {
    const byExpertId = new Map()

    criterionScores.forEach((entry) => {
        const expertId = getEntryExpertId(entry)
        const elasticsearchScore = numberOrNull(entry?.elasticsearchScore)
        const logScore = toLogElasticsearchScore(elasticsearchScore)

        if (!expertId || logScore === null) return

        const currentScore = byExpertId.get(expertId)
        if (currentScore === undefined || logScore > currentScore) {
            byExpertId.set(expertId, logScore)
        }
    })

    return byExpertId
}

const isVeryGoodFit = (evaluation) => {
    if (!evaluation) return false
    if (evaluation.verdict === 'Very good fit') return true
    const score = Number(evaluation.score)
    return Number.isFinite(score) && score >= 80
}

const isNotEnoughContext = (evaluation) =>
    String(evaluation?.verdict || '').trim().toLowerCase() === 'not enough context'

const buildCriterionCombinedHistogramData = (
    criterionScores = [],
    completedEvaluationExpertIds,
    evaluationsByExpertId,
    bandCount = CRITERION_HISTOGRAM_BAND_COUNT
) => {
    const logScoreByExpertId = buildLogScoreByExpertIdForCriterion(criterionScores)
    if (logScoreByExpertId.size === 0) return []

    const logValues = Array.from(logScoreByExpertId.values())
    const minLogScore = Math.min(...logValues)
    const maxLogScore = Math.max(...logValues)
    const logScoreStep = Math.max((maxLogScore - minLogScore) / bandCount, Number.EPSILON)

    const buckets = Array.from({ length: bandCount }, (_, index) => {
        const logScoreMin = minLogScore + index * logScoreStep
        const logScoreMax =
            index === bandCount - 1 ? maxLogScore : minLogScore + (index + 1) * logScoreStep

        return {
            label: `${logScoreMin.toFixed(2)}-${logScoreMax.toFixed(2)}`,
            logScoreMin,
            logScoreMax,
            veryGoodFitCount: 0,
            noContextCount: 0,
            otherEvaluatedCount: 0,
            notYetEvaluatedCount: 0,
            esMin: null,
            esMax: null,
        }
    })

    criterionScores.forEach((entry) => {
        const expertId = getEntryExpertId(entry)
        const elasticsearchScore = numberOrNull(entry?.elasticsearchScore)
        const logScore = logScoreByExpertId.get(expertId)

        if (!expertId || elasticsearchScore === null || logScore === undefined) return

        const relativeIndex = Math.floor((logScore - minLogScore) / logScoreStep)
        const bucketIndex = Math.max(0, Math.min(bandCount - 1, relativeIndex))
        const bucket = buckets[bucketIndex]

        if (bucket.esMin === null || elasticsearchScore < bucket.esMin) bucket.esMin = elasticsearchScore
        if (bucket.esMax === null || elasticsearchScore > bucket.esMax) bucket.esMax = elasticsearchScore

        if (completedEvaluationExpertIds.has(expertId)) {
            const evaluation = evaluationsByExpertId[expertId]
            if (isVeryGoodFit(evaluation)) {
                bucket.veryGoodFitCount += 1
            } else if (isNotEnoughContext(evaluation)) {
                bucket.noContextCount += 1
            } else {
                bucket.otherEvaluatedCount += 1
            }
        } else {
            bucket.notYetEvaluatedCount += 1
        }
    })

    return buckets
}

const buildCriterionScatterPoints = (
    criterionScores = [],
    completedEvaluationExpertIds,
    evaluationsByExpertId,
) => {
    const logScoreByExpertId = buildLogScoreByExpertIdForCriterion(criterionScores)
    if (logScoreByExpertId.size === 0) return []

    let highestUnevaluatedLogScore = null

    criterionScores.forEach((entry) => {
        const expertId = getEntryExpertId(entry)
        if (!expertId || completedEvaluationExpertIds.has(expertId)) return

        const logScore = logScoreByExpertId.get(expertId)
        if (logScore === undefined) return

        if (highestUnevaluatedLogScore === null || logScore > highestUnevaluatedLogScore) {
            highestUnevaluatedLogScore = logScore
        }
    })

    const logScoreCutoff = highestUnevaluatedLogScore === null ? 0 : highestUnevaluatedLogScore

    return criterionScores
        .map((entry) => {
            const expertId = getEntryExpertId(entry)
            if (!expertId || !completedEvaluationExpertIds.has(expertId)) return null

            const logScore = logScoreByExpertId.get(expertId)
            if (logScore === undefined || logScore < logScoreCutoff) return null

            const evaluation = evaluationsByExpertId[expertId]
            if (!evaluation || evaluation.verdict === 'Not enough context') return null

            const evaluatedScore = numberOrNull(evaluation.score)
            if (evaluatedScore === null) return null

            return {
                x: logScore,
                y: evaluatedScore,
                expertId,
                expertName: evaluation.expertName || null,
            }
        })
        .filter(Boolean)
}

const ScoreTooltip = ({
    active,
    payload,
    hoveredExpertId,
    expertSummaryById,
    expertSummaryLoadingById,
}) => {
    if (!active) return null

    const point = extractExpertPointFromTooltipPayload(payload || [])
    if (!point) return null
    if (!hoveredExpertId || point.expertId !== hoveredExpertId) return null

    const expertId = point.expertId
    const summary = expertSummaryById[expertId]
    const isLoading = Boolean(expertSummaryLoadingById[expertId])
    const expertName = summary?.fullName || point.expertName || expertId
    const country = summary?.country || 'N/A'
    const credentials = formatList(summary?.credentials || [])

    return (
        <div style={TOOLTIP_CARD_STYLE}>
            <div><strong>Expert:</strong> {expertName}{isLoading ? ' (loading...)' : ''}</div>
            <div><strong>Expert ID:</strong> {expertId}</div>
            <div><strong>Country:</strong> {country}</div>
            <div><strong>Credentials:</strong> {credentials}</div>
            <div><strong>ln(ES score + 1):</strong> {Number(point.x).toFixed(3)}</div>
            <div><strong>Evaluated score:</strong> {Number(point.y).toFixed(2)}</div>
        </div>
    )
}

const ScoreChart = ({
    points,
    onPointHover,
    expertSummaryById,
    expertSummaryLoadingById,
}) => {
    const [hoveredExpertId, setHoveredExpertId] = useState(null)

    const chartMetrics = useMemo(() => {
        if (points.length === 0) {
            return {
                spearmanCorrelation: null,
                kendallTau: null,
                pearsonCorrelation: null,
                xDomainMin: 0,
                xDomainMax: 1,
                lineData: [],
                isKendallSkipped: false,
            }
        }

        const regression = computeRegression(points)
        const spearmanCorrelation = computeSpearmanCorrelation(points)
        const kendallTau =
            points.length > MAX_POINTS_FOR_KENDALL_TAU ? null : computeKendallTau(points)
        const pearsonCorrelation = computePearsonCorrelationFromArrays(
            points.map((point) => point.x),
            points.map((point) => point.y),
        )

        const xValues = points.map((point) => point.x)
        const minX = Math.min(...xValues)
        const maxX = Math.max(...xValues)
        const range = maxX - minX
        const leftPadding = Math.max(0.15, range * 0.12)
        const rightPadding = Math.max(0.15, range * 0.05)
        const xDomainMin = minX - leftPadding
        const xDomainMax = maxX + rightPadding
        const lineData = regression
            ? [
                  { x: xDomainMin, y: regression.slope * xDomainMin + regression.intercept },
                  { x: maxX, y: regression.slope * maxX + regression.intercept },
              ]
            : []

        return {
            spearmanCorrelation,
            kendallTau,
            pearsonCorrelation,
            xDomainMin,
            xDomainMax,
            lineData,
            isKendallSkipped: points.length > MAX_POINTS_FOR_KENDALL_TAU,
        }
    }, [points])

    const kendallLabel = chartMetrics.isKendallSkipped
        ? `Skipped (n>${MAX_POINTS_FOR_KENDALL_TAU})`
        : chartMetrics.kendallTau === null
          ? 'N/A'
          : chartMetrics.kendallTau.toFixed(3)

    if (points.length === 0) {
        return <Message info content="No valid Elasticsearch/evaluation score pairs available yet." />
    }

    return (
        <div style={{ width: '100%', height: 260 }}>
            <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '0.4rem' }}>
                {`Spearman rho: ${chartMetrics.spearmanCorrelation === null ? 'N/A' : chartMetrics.spearmanCorrelation.toFixed(3)} | Kendall tau: ${kendallLabel} | Pearson r: ${chartMetrics.pearsonCorrelation === null ? 'N/A' : chartMetrics.pearsonCorrelation.toFixed(3)}`}
            </div>
            <ResponsiveContainer>
                <ComposedChart margin={{ top: 8, right: 16, bottom: 16, left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        type="number"
                        dataKey="x"
                        name="ln(ES score + 1)"
                        scale="linear"
                        domain={[chartMetrics.xDomainMin, chartMetrics.xDomainMax]}
                        tickCount={12}
                        tickFormatter={(value) => Number(value).toFixed(2)}
                        tick={{ fontSize: 12 }}
                        label={{
                            value: 'ln(ES score + 1)',
                            position: 'insideBottom',
                            offset: -6,
                            style: { fontSize: 12 },
                        }}
                    />
                    <YAxis
                        type="number"
                        dataKey="y"
                        name="Evaluated score"
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        label={{
                            value: 'Evaluated score',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fontSize: 12 },
                        }}
                    />
                    <Tooltip
                        content={(tooltipProps) => (
                            <ScoreTooltip
                                {...tooltipProps}
                                hoveredExpertId={hoveredExpertId}
                                expertSummaryById={expertSummaryById}
                                expertSummaryLoadingById={expertSummaryLoadingById}
                            />
                        )}
                    />
                    <Legend />
                    <Scatter
                        name="Experts"
                        data={points}
                        dataKey="y"
                        fill="#1B5E82"
                        onMouseEnter={(entry) => {
                            const point = entry?.payload || entry
                            const expertId = point?.expertId
                            if (expertId) {
                                setHoveredExpertId(expertId)
                                onPointHover(expertId)
                            }
                        }}
                        onMouseLeave={() => setHoveredExpertId(null)}
                    />
                    {chartMetrics.lineData.length > 0 && (
                        <Line
                            name="Linear regression"
                            type="linear"
                            data={chartMetrics.lineData}
                            dataKey="y"
                            stroke="#f47560"
                            dot={false}
                            isAnimationActive={false}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

const CriterionHistogramTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null

    const bucket = payload[0]?.payload
    if (!bucket) return null

    const esRange = bucket.esMin === null || bucket.esMax === null
        ? 'N/A'
        : `${bucket.esMin.toFixed(2)} - ${bucket.esMax.toFixed(2)}`

    return (
        <div style={TOOLTIP_CARD_STYLE}>
            <div><strong>ln(ES score + 1) range:</strong> {`${bucket.logScoreMin.toFixed(2)} - ${bucket.logScoreMax.toFixed(2)}`}</div>
            <div><strong>ES score range:</strong> {esRange}</div>
            <div><strong>Very good fit:</strong> {bucket.veryGoodFitCount}</div>
            <div><strong>Other evaluated:</strong> {bucket.otherEvaluatedCount}</div>
            <div><strong>No context:</strong> {bucket.noContextCount}</div>
            <div><strong>Not yet evaluated:</strong> {bucket.notYetEvaluatedCount}</div>
        </div>
    )
}

const CriterionCombinedHistogram = ({ criterionScores, completedEvaluationExpertIds, evaluationsByExpertId }) => {
    const histogramData = useMemo(
        () =>
            buildCriterionCombinedHistogramData(
                criterionScores,
                completedEvaluationExpertIds,
                evaluationsByExpertId
            ),
        [criterionScores, completedEvaluationExpertIds, evaluationsByExpertId]
    )

    if (histogramData.length === 0) {
        return <Message info content="No Elasticsearch scores available for this criterion." />
    }

    return (
        <div style={{ width: '100%', height: 280, marginBottom: '1rem' }}>
            <ResponsiveContainer>
                <BarChart data={histogramData} margin={{ top: 8, right: 16, bottom: 16, left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="label"
                        interval="preserveStartEnd"
                        minTickGap={14}
                        tick={{ fontSize: 11 }}
                        label={{
                            value: 'ln(ES score + 1) buckets (within criterion)',
                            position: 'insideBottom',
                            offset: -6,
                            style: { fontSize: 12 },
                        }}
                    />
                    <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 12 }}
                        label={{
                            value: 'Expert count',
                            angle: -90,
                            position: 'insideLeft',
                            style: { fontSize: 12 },
                        }}
                    />
                    <Tooltip content={<CriterionHistogramTooltip />} />
                    <Legend />
                    <Bar
                        name="Not yet evaluated"
                        dataKey="notYetEvaluatedCount"
                        stackId="criterion"
                        fill="#B0BEC5"
                    />
                    <Bar
                        name="No context"
                        dataKey="noContextCount"
                        stackId="criterion"
                        fill="#f59e0b"
                    />
                    <Bar
                        name="Other evaluated"
                        dataKey="otherEvaluatedCount"
                        stackId="criterion"
                        fill="#6ba3bf"
                    />
                    <Bar
                        name="Very good fit"
                        dataKey="veryGoodFitCount"
                        stackId="criterion"
                        fill="#1B5E82"
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

// Memoizes point construction per criterion instance, mirroring CriterionCombinedHistogram.
const CriterionScatterChart = ({
    criterionScores,
    completedEvaluationExpertIds,
    evaluationsByExpertId,
    onPointHover,
    expertSummaryById,
    expertSummaryLoadingById,
}) => {
    const points = useMemo(
        () =>
            buildCriterionScatterPoints(
                criterionScores,
                completedEvaluationExpertIds,
                evaluationsByExpertId
            ),
        [criterionScores, completedEvaluationExpertIds, evaluationsByExpertId]
    )

    return (
        <ScoreChart
            points={points}
            onPointHover={onPointHover}
            expertSummaryById={expertSummaryById}
            expertSummaryLoadingById={expertSummaryLoadingById}
        />
    )
}

const ProjectRecommendationsSection = ({ projectId }) => {
    const apolloClient = useApolloClient()
    const [expandedCriteriaIds, setExpandedCriteriaIds] = useState(null)
    const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false)
    const [continueConfirmCriterionId, setContinueConfirmCriterionId] = useState(null)
    const [pendingContinueCriterionId, setPendingContinueCriterionId] = useState(null)
    const [expertSummaryById, setExpertSummaryById] = useState({})
    const [expertSummaryLoadingById, setExpertSummaryLoadingById] = useState({})

    const [loadRecommendations, {
        called: recommendationsCalled,
        data: recommendationsData,
        loading: recommendationsLoading,
        refetch: refetchRecommendations,
    }] = useLazyQuery(PROJECT_RECOMMENDATIONS_QUERY, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    })

    useEffect(() => {
        if (!projectId) return
        loadRecommendations({ variables: { id: projectId } })
    }, [projectId, loadRecommendations])

    const [runRecommendations, { loading: isRunningRecommendations }] = useMutation(
        RUN_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION
    )

    const [continueRecommendations, { loading: isContinuingRecommendations }] = useMutation(
        CONTINUE_PROJECT_EXPERT_RECOMMENDATIONS_MUTATION,
        {
            onError: () => {
                setPendingContinueCriterionId(null)
            },
        }
    )

    const refreshRecommendations = async () => {
        if (!projectId) return

        if (refetchRecommendations) {
            await refetchRecommendations()
            return
        }

        await loadRecommendations({ variables: { id: projectId }, fetchPolicy: 'network-only' })
    }

    const recommendations = recommendationsData?.project?.expertRecommendations
    const hasRecommendations = Boolean(recommendations)
    const criteria = useMemo(
        () => (Array.isArray(recommendations?.criteria) ? recommendations.criteria : []),
        [recommendations]
    )
    const criteriaWithIds = useMemo(
        () =>
            criteria.map((criterion, index) => ({
                ...criterion,
                _criterionId: criterion.criterionId || `criterion-${index + 1}`,
            })),
        [criteria]
    )
    const defaultExpandedCriterionIds = useMemo(
        () => criteriaWithIds.map((criterion) => criterion._criterionId),
        [criteriaWithIds]
    )
    const evaluations = useMemo(
        () => (Array.isArray(recommendations?.evaluations) ? recommendations.evaluations : []),
        [recommendations]
    )

    const completedEvaluations = useMemo(
        () => evaluations.filter((evaluation) => evaluation?.evaluationStatus === 'completed'),
        [evaluations]
    )

    const evaluationsByExpertId = useMemo(
        () =>
            completedEvaluations.reduce((acc, evaluation) => {
                acc[evaluation.expertId] = evaluation
                return acc
            }, {}),
        [completedEvaluations]
    )

    const completedEvaluationExpertIds = useMemo(
        () => new Set(completedEvaluations.map((evaluation) => evaluation.expertId)),
        [completedEvaluations]
    )

    const histogramEvaluationsByExpertId = useMemo(
        () =>
            evaluations.reduce((acc, evaluation) => {
                const expertId = String(evaluation?.expertId || '').trim()
                if (!expertId) return acc

                if (
                    evaluation?.evaluationStatus === 'completed' ||
                    evaluation?.verdict === 'Not enough context'
                ) {
                    acc[expertId] = evaluation
                }

                return acc
            }, {}),
        [evaluations]
    )

    const histogramEvaluatedExpertIds = useMemo(
        () => new Set(Object.keys(histogramEvaluationsByExpertId)),
        [histogramEvaluationsByExpertId]
    )

    const highestLogScoreByExpertId = useMemo(() => {
        const byExpertId = new Map()

        criteriaWithIds.forEach((criterion) => {
            const logScoreByExpertId = buildLogScoreByExpertIdForCriterion(
                criterion.expertScores || []
            )

            logScoreByExpertId.forEach((logScore, expertId) => {
                const currentScore = byExpertId.get(expertId)
                if (currentScore === undefined || logScore > currentScore) {
                    byExpertId.set(expertId, logScore)
                }
            })
        })

        return byExpertId
    }, [criteriaWithIds])

    const aggregatedScatterPoints = useMemo(
        () =>
            Array.from(highestLogScoreByExpertId.entries())
                .map(([expertId, logScore]) => {
                    const evaluation = evaluationsByExpertId[expertId]
                    if (!evaluation || evaluation.verdict === 'Not enough context') {
                        return null
                    }

                    const evaluatedScore = numberOrNull(evaluation.score)
                    if (evaluatedScore === null) return null

                    return {
                        x: logScore,
                        y: evaluatedScore,
                        expertId,
                        expertName: evaluation.expertName || null,
                    }
                })
                .filter(Boolean),
        [highestLogScoreByExpertId, evaluationsByExpertId]
    )

    const toggleCriterion = (criterionId) => {
        const currentExpandedIds = expandedCriteriaIds || defaultExpandedCriterionIds
        setExpandedCriteriaIds((current) =>
            (current || currentExpandedIds).includes(criterionId)
                ? (current || currentExpandedIds).filter((id) => id !== criterionId)
                : [...(current || currentExpandedIds), criterionId]
        )
    }

    const triggerRun = async (rerun) => {
        await runRecommendations({
            variables: {
                projectId,
                rerun,
            },
        })
        await refreshRecommendations()
    }

    const triggerContinue = async (criterionId) => {
        setPendingContinueCriterionId(criterionId)
        try {
            await continueRecommendations({
                variables: {
                    projectId,
                    criterionId,
                },
            })
            await refreshRecommendations()
        } finally {
            setPendingContinueCriterionId(null)
        }
    }

    const loadExpertSummaryForTooltip = async (expertId) => {
        if (!expertId) return

        if (expertSummaryById[expertId] || expertSummaryLoadingById[expertId]) {
            return
        }

        setExpertSummaryLoadingById((current) => ({
            ...current,
            [expertId]: true,
        }))

        try {
            const { data } = await apolloClient.query({
                query: LOAD_EXPERT_SUMMARY_QUERY,
                variables: { id: expertId },
                fetchPolicy: 'network-only',
            })

            const fetchedSummary = data?.expert || {}
            setExpertSummaryById((current) => ({
                ...current,
                [expertId]: {
                    fullName: fetchedSummary.fullName || expertId,
                    country: fetchedSummary.country || null,
                    credentials: fetchedSummary.credentials || [],
                    functions: fetchedSummary.functions || [],
                    medical_specialties: fetchedSummary.medical_specialties || [],
                    technology_tags: fetchedSummary.technology_tags || [],
                },
            }))
        } catch {
            // Tooltip hover should not throw/unhandled-reject if the expert lookup fails.
        } finally {
            setExpertSummaryLoadingById((current) => {
                const next = { ...current }
                delete next[expertId]
                return next
            })
        }
    }

    const generationStatus = recommendations?.generationStatus || 'not_started'
    const uniqueExpertCount = recommendations?.uniqueExpertIds?.length || 0
    const completedCount = completedEvaluations.length
    const completionFormulaNote =
        generationStatus === 'completed' ? ' Normalization formula: ln(ES score + 1).' : ''
    const isRecommendationsReady = recommendationsCalled && !recommendationsLoading

    return (
        <Segment
            raised
            style={{
                marginTop: '2rem',
                padding: '1.5rem',
                borderTop: '5px solid #1B5E82',
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
            }}
        >
            <Header as="h2" dividing style={{ fontSize: '2rem', marginBottom: '1rem' }}>
                Recommendations
            </Header>
            {!isRecommendationsReady ? (
                <Message info content="Loading recommendations..." />
            ) : hasRecommendations ? (
                <Message
                    info
                    content={`Recommendations exist. Status: ${generationStatus}. Completed evaluations: ${completedCount}/${uniqueExpertCount}.${completionFormulaNote}`}
                />
            ) : (
                <Message warning content="No recommendations currently exist for this project." />
            )}

            <Button.Group>
                {!hasRecommendations ? (
                    <Button
                        primary
                        loading={isRunningRecommendations}
                        disabled={
                            !isRecommendationsReady ||
                            isRunningRecommendations ||
                            isContinuingRecommendations
                        }
                        onClick={() => triggerRun(false)}
                        content="Run recommendations"
                    />
                ) : (
                    <Button
                        negative
                        loading={isRunningRecommendations}
                        disabled={
                            !isRecommendationsReady ||
                            isRunningRecommendations ||
                            isContinuingRecommendations
                        }
                        onClick={() => setRerunConfirmOpen(true)}
                        content="Rerun recommendations"
                    />
                )}
            </Button.Group>

            <Segment secondary style={{ marginTop: '1.2rem' }}>
                <Header as="h4" style={{ marginBottom: '0.8rem' }}>
                    ln(Elasticsearch Score + 1) vs Evaluated Score (All Criteria)
                </Header>
                {!isRecommendationsReady ? (
                    <Loader active inline="centered" />
                ) : (
                    <ScoreChart
                        points={aggregatedScatterPoints}
                        onPointHover={loadExpertSummaryForTooltip}
                        expertSummaryById={expertSummaryById}
                        expertSummaryLoadingById={expertSummaryLoadingById}
                    />
                )}
            </Segment>

            {criteriaWithIds.map((criterion, index) => {
                const criterionId = criterion._criterionId
                const activeExpandedIds = expandedCriteriaIds || defaultExpandedCriterionIds
                const isExpanded = activeExpandedIds.includes(criterionId)
                const expertScores = criterion.expertScores || []
                const evaluatedCount = Number(criterion.evaluatedCount || 0)
                const totalCount = Number(criterion.total || expertScores.length)

                return (
                    <Segment
                        key={criterionId}
                        style={{
                            marginTop: '1.25rem',
                            border: '1px solid #d7e1ea',
                            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                            padding: '1.1rem 1.2rem',
                        }}
                    >
                        <Button
                            basic
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0.2rem 0',
                                marginBottom: '0.4rem',
                                textAlign: 'left',
                            }}
                            onClick={() => toggleCriterion(criterionId)}
                            aria-label={isExpanded ? 'Collapse criterion' : 'Expand criterion'}
                        >
                            <Header
                                as="h3"
                                style={{
                                    margin: 0,
                                    fontWeight: 800,
                                    fontSize: '1.35rem',
                                    lineHeight: 1.25,
                                    color: '#0f2e45',
                                    letterSpacing: '0.01em',
                                }}
                            >
                                {criterion.description || `Criterion ${index + 1}`}
                            </Header>
                            <Icon
                                name={isExpanded ? 'caret down' : 'caret right'}
                                size="big"
                                style={{ marginLeft: '0.45rem' }}
                            />
                        </Button>

                        <p style={{ marginBottom: isExpanded ? '0.8rem' : 0, color: '#4b5563' }}>
                            {`Search status: ${criterion.searchStatus || 'unknown'} | Evaluated window: ${evaluatedCount}/${totalCount} | Stored experts: ${expertScores.length}`}
                        </p>

                        {isExpanded && (
                            <>
                                <Button
                                    primary
                                    size="small"
                                    loading={
                                        isContinuingRecommendations &&
                                        pendingContinueCriterionId === criterionId
                                    }
                                    disabled={isRunningRecommendations || isContinuingRecommendations}
                                    onClick={() => setContinueConfirmCriterionId(criterionId)}
                                    content="Continue recommendations"
                                />

                                <Segment secondary style={{ marginTop: '1rem' }}>
                                    <Header as="h5">Search Criteria Inputs</Header>
                                    {criterion.searchInputs?.length ? (
                                        <List bulleted>
                                            {criterion.searchInputs.map((input, inputIndex) => (
                                                <List.Item key={`${criterionId}-input-${inputIndex}`}>
                                                    <div>
                                                        <strong>Queries:</strong>{' '}
                                                        {(input.queries || []).join(', ') || 'N/A'}
                                                    </div>
                                                    <div style={{ marginTop: '0.3rem' }}>
                                                        <strong>Filters:</strong>
                                                        {(input.filters || []).length === 0
                                                            ? ' None'
                                                            : ''}
                                                    </div>
                                                    {(input.filters || []).length > 0 && (
                                                        <List bulleted>
                                                            {input.filters.map((filter, filterIndex) => (
                                                                <List.Item
                                                                    key={`${criterionId}-filter-${inputIndex}-${filterIndex}`}
                                                                >
                                                                    {`${filter.categoryId}: ${(filter.values || []).join(', ')}`}
                                                                </List.Item>
                                                            ))}
                                                        </List>
                                                    )}
                                                </List.Item>
                                            ))}
                                        </List>
                                    ) : (
                                        <p>No search input details available.</p>
                                    )}
                                </Segment>

                                <Header as="h5" style={{ marginTop: '1rem' }}>
                                    ln(Elasticsearch Score + 1) vs Evaluated Score
                                </Header>
                                <CriterionScatterChart
                                    criterionScores={expertScores}
                                    completedEvaluationExpertIds={completedEvaluationExpertIds}
                                    evaluationsByExpertId={evaluationsByExpertId}
                                    onPointHover={loadExpertSummaryForTooltip}
                                    expertSummaryById={expertSummaryById}
                                    expertSummaryLoadingById={expertSummaryLoadingById}
                                />

                                <Header as="h5" style={{ marginTop: '1rem' }}>
                                    Elasticsearch Score Distribution
                                </Header>
                                <CriterionCombinedHistogram
                                    criterionScores={expertScores}
                                    completedEvaluationExpertIds={histogramEvaluatedExpertIds}
                                    evaluationsByExpertId={histogramEvaluationsByExpertId}
                                />

                            </>
                        )}
                    </Segment>
                )
            })}

            <Confirm
                open={rerunConfirmOpen}
                onCancel={() => setRerunConfirmOpen(false)}
                onConfirm={async () => {
                    await triggerRun(true)
                    setRerunConfirmOpen(false)
                }}
                cancelButton="Cancel"
                confirmButton="Confirm rerun"
                content="This will delete existing recommendation state and run the recommendation pipeline again."
                header="Rerun recommendations?"
            />

            <Confirm
                open={Boolean(continueConfirmCriterionId)}
                onCancel={() => setContinueConfirmCriterionId(null)}
                onConfirm={async () => {
                    const criterionId = continueConfirmCriterionId
                    setContinueConfirmCriterionId(null)
                    if (criterionId) {
                        await triggerContinue(criterionId)
                    }
                }}
                cancelButton="Cancel"
                confirmButton="Continue"
                content="This will evaluate the next batch of experts for this criterion based on the stored Elasticsearch ranking."
                header="Continue recommendations?"
            />

            {(isRunningRecommendations || isContinuingRecommendations) && (
                <Loader active inline="centered" style={{ marginTop: '1rem' }} />
            )}
        </Segment>
    )
}

export default ProjectRecommendationsSection
